import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import { useAuth, usePermissions } from "./AuthContext";
import api from "./api";
import { tryLeave } from "./unsavedGuard";
import { WhatsAppSupport } from "./WhatsAppSupport";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, FileText, Send, CreditCard, Building2,
  Mail, LogOut, ChevronDown, Menu, X, Globe, Crosshair, HelpCircle, Building, SlidersHorizontal, Contact2, Megaphone, BarChart3, UserCog, KeyRound,
} from "lucide-react";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/contacts", label: "Contacte", icon: Users },
  { to: "/templates", label: "Sabloane", icon: FileText },
  { to: "/campaigns", label: "Rezumat", icon: Send },
  { to: "/site", label: "Site public", icon: Globe },
  { to: "/billing", label: "Facturare", icon: CreditCard },
];

const hunterItem = { to: "/lead-orders", label: "Hunter", icon: Crosshair };
const tenantCrmItem = { to: "/tenant-crm", label: "CRM", icon: Contact2, testid: "nav-tenant-crm" };
const erpItem = { to: "/erp-yvola", label: "ERP YVOLA", icon: HelpCircle };
const configuratorItem = { to: "/configurator", label: "Configurator", icon: SlidersHorizontal };
const companyItem = { to: "/company-profile", label: "Date firma", icon: Building };
const proformeItem = { to: "/proforme", label: "Proforme emise", icon: FileText };
const campaniiItem = { to: "/campanii", label: "Campanii", icon: Megaphone, testid: "nav-campanii-report" };
const campaniiTenantiItem = { to: "/campanii-tenanti", label: "Campanii tenanti", icon: Megaphone, testid: "nav-campanii-tenanti-report" };
const rapoarteItem = { to: "/rapoarte-campanii", label: "Rapoarte", icon: BarChart3, testid: "nav-rapoarte-campanii" };
const campaniiTenantItem = { to: "/campanii-tenant", label: "Campanii tenant", icon: Crosshair, testid: "nav-campanii-tenant" };
const utilizatoriItem = { to: "/utilizatori", label: "Utilizatori si roluri", icon: UserCog, testid: "nav-utilizatori" };

const MOD_BY_PATH = {
  "/": "dashboard", "/contacts": "contacte", "/templates": "sabloane", "/site": "site",
  "/tenants": "tenanti", "/crm": "crm", "/company-profile": "date_firma", "/campanii": "campanii",
  "/rapoarte-campanii": "rapoarte", "/tenant-crm": "contacte", "/lead-orders": "hunter",
  "/proforme": "proforme", "/utilizatori": "utilizatori", "/campanii-tenanti": "campanii",
};

function TenantSwitcher() {
  const { tenantId, setTenantId } = useAuth();
  const [tenants, setTenants] = useState([]);
  useEffect(() => {
    api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
  }, []);
  return (
    <div className="relative">
      <select
        data-testid="tenant-switcher"
        value={tenantId}
        onChange={(e) => setTenantId(e.target.value)}
        className="appearance-none pl-3 pr-8 py-2 text-sm rounded-lg bg-white border border-slate-200 hover:border-slate-300 outline-none focus:border-emerald-500 cursor-pointer max-w-[220px]"
      >
        <option value="">Selecteaza tenant…</option>
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
        ))}
      </select>
      <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  );
}

function BnrBadge() {
  const [rate, setRate] = useState(null);
  useEffect(() => {
    api.get("/lead-orders/bnr-rate").then(({ data }) => {
      if (data && data.rate != null) setRate(Number(data.rate).toFixed(2));
    }).catch(() => {});
  }, []);
  if (!rate) return null;
  return (
    <div data-testid="bnr-badge" style={{ fontFamily: "Arial", fontSize: "12px", color: "#000", fontWeight: "bold", border: "1px solid #000", padding: "4px 8px", borderRadius: "4px" }}>
      Euro {rate} RON
    </div>
  );
}

export default function Layout() {
  const { user, logout, isSuperAdmin } = useAuth();
  const { can } = usePermissions();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const guardedNav = (to) => (e) => {
    setOpen(false);
    if (tryLeave(() => nav(to))) { e.preventDefault(); }
  };

  const rawItems = isSuperAdmin
    ? [...navItems, { to: "/tenants", label: "Tenanti", icon: Building2 }, { to: "/crm", label: "CRM", icon: Contact2 }, companyItem, utilizatoriItem, campaniiItem, campaniiTenantiItem, campaniiTenantItem, rapoarteItem]
    : [...navItems.filter((i) => !["/campaigns", "/billing", "/contacts", "/templates"].includes(i.to)), tenantCrmItem, erpItem, configuratorItem, companyItem, utilizatoriItem];
  const items = rawItems.filter((it) => { const m = MOD_BY_PATH[it.to]; return !m || can(m, "view"); });

  const doLogout = async () => { await logout(); window.location.replace("/login"); };
  const [resetting, setResetting] = useState(false);
  const requestReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/auth/request-password-reset");
      toast.success(`Link de resetare trimis pe ${data.email || "emailul contului"}`);
    } catch (e) {
      toast.error("Nu s-a putut trimite linkul de resetare");
    } finally { setResetting(false); }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-white text-black border-r border-gray-300 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-gray-300">
          <div className="w-9 h-9 rounded-md border border-gray-300 flex items-center justify-center">
            <Mail className="w-5 h-5 text-black" />
          </div>
          {isSuperAdmin ? (
            <Link to="/home" title="Deschide pagina home"
              className="text-lg font-bold text-black tracking-tight truncate hover:underline" data-testid="sidebar-brand">
              YVOLA
            </Link>
          ) : user?.tenant_slug ? (
            <a href={`/site/${user.tenant_slug}`} target="_blank" rel="noreferrer" title="Vezi pagina vizitator"
              className="text-lg font-bold text-black tracking-tight truncate hover:underline" data-testid="sidebar-brand">
              {user?.tenant_name || "YVOLA"}
            </a>
          ) : (
            <span className="text-lg font-bold text-black tracking-tight truncate" data-testid="sidebar-brand">{user?.tenant_name || "YVOLA"}</span>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
          {items.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to} to={to} end={end}
              data-testid={testid || `nav-${label.toLowerCase()}`}
              onClick={guardedNav(to)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-black text-white" : "text-black hover:bg-gray-100"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-gray-300 shrink-0">
          <div className="px-3 py-2 text-xs text-gray-500 truncate">{user?.email}</div>
          <button
            data-testid="reset-my-password-button"
            onClick={requestReset}
            disabled={resetting}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-black hover:bg-gray-100 transition-colors disabled:opacity-60"
          >
            <KeyRound className="w-[18px] h-[18px]" /> {resetting ? "Se trimite..." : "Reseteaza parola"}
          </button>
          <button
            data-testid="logout-button"
            onClick={() => { if (!tryLeave(() => doLogout())) doLogout(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-black hover:bg-gray-100 transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" /> Deconectare
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-300 flex items-center justify-between px-4 lg:px-6 gap-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <BnrBadge />
            {isSuperAdmin && <TenantSwitcher />}
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center font-bold text-sm text-black">
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="hidden sm:block leading-tight">
                <div className="text-sm font-medium text-black">{user?.name}</div>
                <div className="text-xs text-gray-500 capitalize">{user?.role?.replace("_", " ")}</div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 overflow-auto bg-white">
          <Outlet />
        </main>
      </div>
      <WhatsAppSupport />
    </div>
  );
}
